import { useState } from "react";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { type TierForm, type RuleValue } from "./propFirmTierForm";
import { collectTierProblems } from "./propFirmFormValidation";
import type { PricingModel } from "../../../trading-accounts/pricingModel";
import { SectionHeader } from "./SectionHeader";
import { SELECT_CLASS } from "./formStyles";
import { PhaseEditor } from "./PhaseEditor";
import {
  Copy,
  Trash2,
  ChevronDown,
  ChevronRight,
  Target,
  TrendingDown,
  Wallet,
  Settings2,
  Receipt,
  Plus,
  X,
} from "lucide-react";

// ─── Champ de regle : une seule valeur + son unite ───────────────────────────
//
// Un SEUL input, avec un selecteur d'unite. Remplir a la fois le pourcentage et
// le montant devient impossible par construction : le moteur
// (`propFirmEngine.ts`, lignes 350 et 444) resout le MONTANT en premier et
// ignore alors silencieusement le pourcentage, si bien que deux champs
// cote a cote laissaient l'admin saisir une valeur jamais appliquee.

function RuleInput({ label, rule, onChange, percentPlaceholder, amountPlaceholder, required = false, invalid = false }: {
  label: string;
  rule: RuleValue;
  onChange: (r: RuleValue) => void;
  percentPlaceholder?: string;
  amountPlaceholder?: string;
  required?: boolean;
  invalid?: boolean;
}) {
  return (
    <div>
      <label className="text-muted-foreground block mb-1 text-xs">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <div className="flex gap-1">
        {/*
          Changer d'unite VIDE la valeur. Conserver un `6` saisi pour 6 % en
          ferait un `6 $` que plus rien ne distingue d'une saisie volontaire :
          `ruleToColumns` l'ecrirait tel quel dans la colonne montant, sans le
          moindre avertissement. C'est exactement la classe d'ambiguite que ce
          formulaire existe pour supprimer, donc mieux vaut une ressaisie
          visible qu'une reinterpretation silencieuse.

          Deux boutons bascules plutot qu'un `<select>` : le select natif etait
          rendu sans largeur et en `px-1`, si bien que la fleche native
          recouvrait le glyphe et rendait l'unite active ILLISIBLE. Or c'est la
          donnee la plus lourde de consequences du formulaire (`3000 %` et
          `3000 $` sont deux regles sans rapport). Ici l'unite active est
          toujours visible, et en changer coute un seul clic.

          Le garde `unit !== rule.unit` remplace celui qu'offrait le select :
          un `<button>` se declenche meme si l'unite cliquee est deja active, ce
          qui effacerait une saisie en cours sans que l'admin ait rien change.
        */}
        <div className="flex shrink-0 rounded-md border border-input overflow-hidden" role="group" aria-label={`Unité de ${label}`}>
          {(["percent", "amount"] as const).map(unit => (
            <button
              key={unit}
              type="button"
              onClick={() => { if (unit !== rule.unit) onChange({ unit, value: "" }); }}
              aria-pressed={rule.unit === unit}
              title={unit === "percent" ? `${label} en % de la taille du compte` : `${label} en dollars`}
              className={`w-7 h-8 text-xs font-medium transition-colors ${
                rule.unit === unit
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              {unit === "percent" ? "%" : "$"}
            </button>
          ))}
        </div>
        <Input
          type="text"
          inputMode="decimal"
          value={rule.value}
          onChange={e => onChange({ ...rule, value: e.target.value })}
          placeholder={rule.unit === "amount" ? amountPlaceholder : percentPlaceholder}
          aria-label={label}
          className={`h-8 text-xs flex-1 ${invalid ? "border-destructive" : ""}`}
        />
      </div>
    </div>
  );
}

// ─── TierEditor ───────────────────────────────────────────────────────────────

const DRAWDOWN_TYPE_OPTIONS = [
  { value: "", label: "Aucun / Non défini" },
  { value: "static", label: "Static (balance fixe)" },
  { value: "eod", label: "EOD Trail (fin de journée)" },
  { value: "intraday", label: "Intraday Trail (temps réel)" },
];

export function TierEditor({ tier, index, pricingModel, onChange, onDuplicate, onDelete, onRefresh, onBeforePhaseWrite, phasesByTierId }: {
  tier: TierForm;
  index: number;
  /** Modele de tarification du type de compte parent. Voir `pricingModel.ts`. */
  pricingModel: PricingModel;
  onChange: (t: TierForm) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  /** Sauvegarde le template avant l'ecriture d'une phase. Voir `propFirmPhaseGuard.ts`. */
  onBeforePhaseWrite: () => Promise<boolean>;
  /**
   * Phases du template SERVEUR, par id de palier. Elles ne sont pas dans
   * `tier` : le brouillon du formulaire ne les porte pas (voir
   * `phasesByTierId` dans `propFirmTierForm.ts`), et les lire ici depuis
   * `(tier as any).phases` donnait une liste toujours vide.
   */
  phasesByTierId: Map<string, any[]>;
}) {
  const [open, setOpen] = useState(true);
  const [showOptional, setShowOptional] = useState(false);
  const set = (field: keyof TierForm, value: any) => onChange({ ...tier, [field]: value });
  const problems = collectTierProblems(tier, pricingModel);
  /** Problemes non bloquants : le palier reste enregistrable malgre eux. */
  const warnings = problems.filter(p => !p.blocking);
  const isTrailing = tier.drawdownType === "intraday" || tier.drawdownType === "eod";
  /** Direct funded saute le challenge : pas d'objectif de profit, pas de phase d'evaluation. */
  const directFunded = pricingModel === "direct_funded";
  const phases = tier.id ? phasesByTierId.get(tier.id) ?? [] : [];
  const hasPhases = phases.length > 0;

  /*
    Champs de la section repliable. Une section repliee qui masque une valeur
    saisie serait PIRE que l'etat toujours ouvert d'avant : l'admin croirait le
    palier vide alors qu'il porte un seuil de payout. Le compteur ci-dessous
    est donc la contrepartie du repli, pas une decoration. Il compte les memes
    champs que ceux rendus dans la section, ni plus ni moins.
  */
  const OPTIONAL_FIELDS = [
    "payoutThreshold",
    "payoutCooldownDays",
    "activationFee",
    "fundedConsistencyRulePercent",
    "maxContracts",
    "commissionPerContract",
    "symbolCommissionRows",
  ];
  const optionalFilled = [
    tier.payoutThreshold.value,
    tier.payoutCooldownDays,
    tier.activationFee,
    tier.fundedConsistencyRulePercent,
    tier.maxContracts,
    tier.commissionPerContract,
    tier.symbolCommissionRows.length > 0 ? "x" : "",
  ].filter(v => v.trim() !== "").length;
  /*
    Un probleme sur un champ masque laisserait l'admin chercher a l'aveugle :
    le bandeau nomme le champ, mais le champ n'est pas a l'ecran. La section
    est donc FORCEE ouverte par un OU au rendu, et non via `useState` initial
    ni `useEffect`.

    Pourquoi le OU au rendu : un probleme peut apparaitre APRES le montage
    (l'admin saisit une valeur invalide), ce qu'un etat initial ne rattraperait
    jamais ; et un `useEffect` qui repousse `showOptional` a true se
    redeclencherait tant que le probleme dure, rouvrant la section aussitot que
    l'admin la replie : il se battrait contre lui. Le OU au rendu ne se bat
    contre personne AUJOURD'HUI, parce qu'aucune regle de
    `propFirmFormValidation.ts` ni de `tierRuleValidation.ts` n'emet
    actuellement sur ces sept champs (les champs emis sont label, accountSize,
    profitTarget, drawdownType, maxTotalLoss, trailingDrawdown). C'est une
    garde pour la regle FUTURE : le jour ou elle existe, aucune version du
    repli ne pourra masquer son signalement, meme transitoirement.
  */
  const optionalHasProblem = problems.some(p => OPTIONAL_FIELDS.includes(p.field));
  const optionalOpen = showOptional || optionalHasProblem;

  return (
    <div className="border border-border rounded-md p-3 bg-card text-xs">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm font-medium">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Tier {index + 1}: {tier.label || "(sans nom)"}
          {hasPhases && (
            <span className="ml-2 text-xs font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {phases.length} phase(s)
            </span>
          )}
          {/* Programme live : les comptes "live" de cette prop firm suivent cette phase. */}
          {phases.some((p: { phaseType?: string }) => p.phaseType === "live") && (
            <span className="ml-2 text-xs font-medium text-primary bg-primary/10 border border-primary/40 px-1.5 py-0.5 rounded">
              Live
            </span>
          )}
          {/*
            Pastille visible même replié : le bandeau détaillé vit dans le
            corps, donc replier le palier le masquerait entièrement. Un
            avertissement qu'on ne peut voir qu'en ouvrant chaque palier un par
            un ne serait pas un avertissement.
          */}
          {!open && warnings.length > 0 && (
            <span className="ml-2 text-[11px] font-normal text-warning bg-warning/10 border border-warning/50 px-1.5 py-0.5 rounded">
              Incomplet
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onDuplicate} className="h-6 w-6 p-0" title="Dupliquer ce palier">
            <Copy className="w-3 h-3" />
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onDelete} className="h-6 w-6 p-0 text-destructive" title="Supprimer ce palier">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {open && (
        <div className="space-y-1">
          {/*
            Avertissements NON bloquants : un palier déjà en base à qui il
            manque une donnée métier reste enregistrable (cf. l'asymétrie
            documentée dans `propFirmFormValidation.ts`), mais le moteur
            dégrade alors silencieusement son calcul. On le dit ici, au plus
            près des champs à compléter, plutôt que dans le résumé rouge en
            tête, qui liste uniquement ce qui EMPÊCHE d'enregistrer.
          */}
          {warnings.length > 0 && (
            <div className="rounded-md border border-warning/50 bg-warning/10 p-2 mb-2">
              <p className="text-[11px] text-warning">
                Palier incomplet : {warnings.map(p => p.message).join(" ")}
              </p>
            </div>
          )}

          {/* ── Identification ── */}
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2">
              <label className="text-muted-foreground block mb-1">Label *</label>
              <Input value={tier.label} onChange={e => set("label", e.target.value)} placeholder="$50k" className="h-8 text-xs" />
            </div>
            <div>
              <label className="text-muted-foreground block mb-1">
                Taille du compte ($)<span className="text-destructive ml-0.5">*</span>
              </label>
              <Input
                type="text"
                inputMode="decimal"
                value={tier.accountSize}
                onChange={e => set("accountSize", e.target.value)}
                placeholder="50000"
                className={`h-8 text-xs ${problems.some(p => p.field === "accountSize") ? "border-destructive" : ""}`}
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-1">Min Balance ($)</label>
              <Input type="text" inputMode="decimal" value={tier.minBalance} onChange={e => set("minBalance", parseFloat(e.target.value) || 0)} className="h-8 text-xs" />
            </div>
            <div>
              <label className="text-muted-foreground block mb-1">Max Balance ($)</label>
              <Input type="text" inputMode="decimal" value={tier.maxBalance} onChange={e => set("maxBalance", parseFloat(e.target.value) || 9999999)} className="h-8 text-xs" />
            </div>
          </div>
          <p className="text-muted-foreground text-[10px]">Taille du compte = montant à l'achat ($150k = 150000). C'est la base de calcul de l'objectif de challenge. Min/Max Balance ne servent qu'à détecter automatiquement ce tier selon la balance initiale du compte.</p>

          {/* ── Prix ── */}
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2">
              <label className="text-muted-foreground block mb-1">
                {pricingModel === "subscription" ? "Prix mensuel ($)" : "Prix ($)"}
              </label>
              <Input
                type="text"
                inputMode="decimal"
                value={tier.price}
                onChange={e => set("price", e.target.value)}
                placeholder="ex: 99"
                className={`h-8 text-xs ${problems.some(p => p.field === "price") ? "border-destructive" : ""}`}
              />
            </div>
          </div>

          {/*
            Les champs plats restent VISIBLES quand le palier a des phases. La
            version d'origine les masquait, mais cette branche n'avait jamais
            tourné (la liste de phases était toujours vide) : activée, elle
            aurait rendu inéditables des champs que le moteur lit encore
            (frais d'activation, seuil de payout, repli d'objectif quand la
            phase n'en fixe pas) et laissé les avertissements pointer vers des
            champs hors écran.
          */}
          {hasPhases && (
            <div className="mt-2 p-3 bg-muted/20 rounded-md">
              <p className="text-xs text-muted-foreground">
                Ce palier a des phases : le moteur applique en priorité les règles de la phase active (objectif, drawdown, jours). Les champs ci-dessous servent de repli, et restent la source des frais d'activation et du seuil de payout.
              </p>
            </div>
          )}
          <>
              {/*
                Section masquee, PAS videe : `profitTarget`, `minTradingDays`
                et `consistencyRulePercent` restent dans `tier` et continuent
                d'etre envoyes tels quels au submit (voir `tierFormToData`).
                Si l'admin rebascule ce type de compte en challenge classique,
                ces valeurs reapparaissent intactes -- rien n'est perdu en
                masquant simplement la section plutot qu'en reinitialisant ces
                champs a chaque passage en direct funded.
              */}
              {!directFunded && (
                <>
                  {/* ── Challenge ── */}
                  <SectionHeader icon={<Target className="w-3 h-3" />} label="Challenge" />
                  <div className="grid grid-cols-2 gap-2">
                    <RuleInput
                      label="Profit Target"
                      rule={tier.profitTarget}
                      onChange={r => set("profitTarget", r)}
                      percentPlaceholder="ex: 6"
                      amountPlaceholder="ex: 3000"
                      required
                      invalid={problems.some(p => p.field === "profitTarget")}
                    />
                    <div>
                      <label className="text-muted-foreground block mb-1">Min Trading Days</label>
                      <Input type="text" inputMode="numeric" value={tier.minTradingDays} onChange={e => set("minTradingDays", e.target.value)} placeholder="ex: 5" className="h-8 text-xs" />
                    </div>
                    {/* Consistance CHALLENGE seulement : la regle funded a son
                        propre champ plus bas. Beaucoup de firms n'en imposent
                        aucune en challenge (Apex Legacy) : laisser vide. */}
                    <div>
                      <label className="text-muted-foreground block mb-1">Consistance challenge %</label>
                      <Input type="text" inputMode="decimal" value={tier.consistencyRulePercent} onChange={e => set("consistencyRulePercent", e.target.value)} placeholder="vide = aucune" className="h-8 text-xs" />
                    </div>
                  </div>
                </>
              )}

              {/* ── Drawdown ── */}
              <SectionHeader icon={<TrendingDown className="w-3 h-3" />} label="Drawdown" />
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="text-muted-foreground block mb-1">
                    Type de drawdown<span className="text-destructive ml-0.5">*</span>
                  </label>
                  <select
                    value={tier.drawdownType}
                    onChange={e => set("drawdownType", e.target.value)}
                    className={`${SELECT_CLASS} ${problems.some(p => p.field === "drawdownType") ? "border-destructive" : ""}`}
                  >
                    {DRAWDOWN_TYPE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <RuleInput
                  label="Max Daily Loss"
                  rule={tier.maxDailyLoss}
                  onChange={r => set("maxDailyLoss", r)}
                  percentPlaceholder="ex: 5"
                  amountPlaceholder="ex: 2000"
                  invalid={problems.some(p => p.field === "maxDailyLoss")}
                />
                <RuleInput
                  label="Max Total Loss"
                  rule={tier.maxTotalLoss}
                  onChange={r => set("maxTotalLoss", r)}
                  percentPlaceholder="ex: 10"
                  amountPlaceholder="ex: 5000"
                  // Requis SEULEMENT en drawdown static : c'est le seul cas ou
                  // son absence rend la regle de perte totale inapplicable.
                  required={tier.drawdownType === "static"}
                  invalid={problems.some(p => p.field === "maxTotalLoss")}
                />
                {isTrailing && (
                  <RuleInput
                    label="Trailing Drawdown"
                    rule={tier.trailingDrawdown}
                    onChange={r => set("trailingDrawdown", r)}
                    percentPlaceholder="ex: 3"
                    amountPlaceholder="ex: 1500"
                    invalid={problems.some(p => p.field === "trailingDrawdown")}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={() => setShowOptional(!showOptional)}
                className="flex items-center gap-1 text-xs text-muted-foreground mt-2 hover:text-foreground"
              >
                {optionalOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Payout et limites
                {optionalFilled > 0 && (
                  <span className="ml-1 bg-muted px-1.5 py-0.5 rounded text-[10px]">
                    {optionalFilled} renseigné(s)
                  </span>
                )}
              </button>
              {optionalOpen && (
                <>
                {/* ── Funded / Payout ── */}
                <SectionHeader icon={<Wallet className="w-3 h-3" />} label="Funded / Payout" />
                <div className="grid grid-cols-2 gap-2">
                  <RuleInput
                    label="Payout Threshold"
                    rule={tier.payoutThreshold}
                    onChange={r => set("payoutThreshold", r)}
                    percentPlaceholder="ex: 10"
                    amountPlaceholder="ex: 500"
                    invalid={problems.some(p => p.field === "payoutThreshold")}
                  />
                  <div>
                    <label className="text-muted-foreground block mb-1">Payout Cooldown (jours)</label>
                    <Input type="text" inputMode="numeric" value={tier.payoutCooldownDays} onChange={e => set("payoutCooldownDays", e.target.value)} placeholder="ex: 14" className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-muted-foreground block mb-1">Activation Fee ($)</label>
                    <Input type="text" inputMode="decimal" value={tier.activationFee} onChange={e => set("activationFee", e.target.value)} placeholder="ex: 149" className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-muted-foreground block mb-1">Consistance funded %</label>
                    <Input type="text" inputMode="decimal" value={tier.fundedConsistencyRulePercent} onChange={e => set("fundedConsistencyRulePercent", e.target.value)} placeholder="ex: 30" className="h-8 text-xs" />
                  </div>
                </div>

                {/* ── Limites ── */}
                <SectionHeader icon={<Settings2 className="w-3 h-3" />} label="Limites & Contraintes" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-muted-foreground block mb-1">Max Contracts</label>
                    <Input type="text" inputMode="numeric" value={tier.maxContracts} onChange={e => set("maxContracts", e.target.value)} placeholder="ex: 10" className="h-8 text-xs" />
                  </div>
                </div>

                {/* ── Commissions (surcharge firm) ── */}
                <SectionHeader icon={<Receipt className="w-3 h-3" />} label="Commissions (surcharge firm)" />
                <p className="text-[11px] text-muted-foreground/70 italic">
                  Vide = taux de la prop firm. Ne saisir ici que ce qui diffère (ex. plan live).
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-muted-foreground">Commission/contrat ($/RT)</label>
                    <Input type="text" inputMode="decimal" value={tier.commissionPerContract}
                      onChange={e => set("commissionPerContract", e.target.value)}
                      placeholder="vide = taux firm" className="h-8 text-xs" />
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] text-muted-foreground">Par symbole (préfixe)</label>
                    <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-[11px]"
                      onClick={() => set("symbolCommissionRows", [...tier.symbolCommissionRows, { prefix: "", rate: "" }])}>
                      <Plus className="w-3 h-3 mr-1" /> Ajouter
                    </Button>
                  </div>
                  {tier.symbolCommissionRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Input placeholder="NQ" value={row.prefix} aria-label="Préfixe symbole" className="w-20 h-7 text-xs font-mono"
                        onChange={e => set("symbolCommissionRows",
                          tier.symbolCommissionRows.map((r, j) => j === i ? { ...r, prefix: e.target.value.toUpperCase() } : r))} />
                      <span className="text-muted-foreground text-xs">→</span>
                      <Input type="text" inputMode="decimal" placeholder="3,5" value={row.rate} aria-label="Taux ($/RT)" className="w-20 h-7 text-xs"
                        onChange={e => set("symbolCommissionRows",
                          tier.symbolCommissionRows.map((r, j) => j === i ? { ...r, rate: e.target.value } : r))} />
                      <span className="text-[11px] text-muted-foreground">$/RT</span>
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        title="Supprimer cette ligne"
                        onClick={() => set("symbolCommissionRows", tier.symbolCommissionRows.filter((_, j) => j !== i))}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                </>
              )}
            </>

          {/* ── Section Phases (uniquement si le tier est déjà sauvegardé en BDD) ── */}
          {tier.id ? (
            <PhaseEditor
              tierId={tier.id}
              phases={phases}
              allowEvaluation={!directFunded}
              onPhaseSaved={onRefresh}
              onPhaseDeleted={onRefresh}
              onBeforePhaseWrite={onBeforePhaseWrite}
            />
          ) : (
            <p className="text-xs text-muted-foreground mt-4 border-t border-border/40 pt-3">
              Sauvegardez le tier pour configurer les phases.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
